import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import BankTicketsView from './BankTicketsView';
import { fetchBankMonitoring } from '../../api/bankMonitoring';
import './BanquesConnecteResults.css';

// Traduit le radio FR (Tout/Groupe/Superviseur) vers ce qu'attend l'API
// (meme convention "All/Group/Supervisor" que le reste de l'app, cf. banks/views.py:BankMonitoringView).
const apiFilter = { Tout: 'All', Groupe: 'Group', Superviseur: 'Supervisor' };

const statutToStatus = {
  'En ligne': 'online',
  'Deconnecte': 'offline',
  "N'a pas connecte": 'never',
};

const formatMoney = (v) => Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 });
const formatTime = (iso) => (iso ? new Date(iso).toLocaleTimeString() : '');

// Pas de heartbeat cote app mobile pour l'instant — "en ligne" est juste "a eu un
// mouvement il y a moins de X minutes" (X = "Minutes a considerer deconnecte" du
// formulaire), pas un vrai signal de presence temps reel. Cf. BankMonitoringView.
const getStatus = (row, offlineMinutes) => {
  if (!row.last_movement) return 'never';
  const diffMin = (Date.now() - new Date(row.last_movement).getTime()) / 60000;
  return diffMin <= Number(offlineMinutes) ? 'online' : 'offline';
};

const BanquesConnecteResults = ({ filter, scopeId, lottery, date, refreshEvery, statut, inColumn, offlineMinutes, onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState(null); // { row, x, y }
  const [selectedBankId, setSelectedBankId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchBankMonitoring({ filter: apiFilter[filter], scopeId, date, tirage: lottery })
      .then((data) => { if (!cancelled) setRows(data); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les banques — verifie que le serveur backend tourne.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter, scopeId, date, lottery]);

  const activeRows = useMemo(
    () => rows
      .filter(r => r.sale_enabled)
      .filter(r => String(lottery ?? '0') === '0' || Number(r.ticket_count) > 0)
      .map(r => ({ ...r, status: getStatus(r, offlineMinutes) })),
    [rows, offlineMinutes, lottery]
  );
  const inactiveCount = rows.length - activeRows.length;

  const statusFilter = statutToStatus[statut] || null;
  const filtered = statusFilter ? activeRows.filter(r => r.status === statusFilter) : activeRows;

  // Decoupees en 1 seule colonne quand "Dans une colonne" est coche, sinon jusqu'a
  // 5 colonnes (meme mise en page que le screenshot original) — jamais plus de
  // colonnes que de banques a afficher, pour ne pas laisser de blocs vides.
  const columns = useMemo(() => {
    const n = inColumn ? Math.min(1, filtered.length) || 1 : Math.min(5, filtered.length) || 1;
    const chunkSize = Math.ceil(filtered.length / n) || 1;
    const cols = [];
    for (let i = 0; i < n; i++) {
      const slice = filtered.slice(i * chunkSize, (i + 1) * chunkSize);
      if (slice.length) cols.push(slice);
    }
    return cols;
  }, [filtered, inColumn]);

  const sumTotal = (list) => list.reduce((s, r) => s + Number(r.total), 0);
  const onlineRows = activeRows.filter(r => r.status === 'online');
  const offlineRows = activeRows.filter(r => r.status === 'offline');
  const neverRows = activeRows.filter(r => r.status === 'never');

  const handleEnter = (row, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ row, x: rect.left, y: rect.bottom });
  };

  if (selectedBankId !== null) {
    return (
      <BankTicketsView
        bankId={selectedBankId}
        date={date}
        lottery={lottery}
        onBack={() => setSelectedBankId(null)}
      />
    );
  }

  return (
    <Layout>
      <button className="bcr-back-btn" onClick={onBack}>Arriere</button>

      <div className="bcr-header">
        <h2 className="bcr-title">Translation missing: fr.all_lottery - Monitoring de la Banque</h2>
        <div className="bcr-subtitle">Rafraichir Chaque {refreshEvery} Scds.</div>
      </div>

      {error && <p className="bcr-error">{error}</p>}
      {loading && <p className="bcr-loading">Chargement...</p>}

      {!loading && !error && (
        <>
          <div className="bcr-stats-bar">
            <span className="bcr-chip bcr-chip-inactif">{inactiveCount}</span> = Inactif
            <button className="bcr-consultez-btn">Consultez</button>
            <span className="bcr-chip bcr-chip-online">{onlineRows.length}</span> = En ligne <strong>{formatMoney(sumTotal(onlineRows))}</strong>
            <span className="bcr-chip bcr-chip-offline">{offlineRows.length}</span> = Deconnecte <strong>{formatMoney(sumTotal(offlineRows))}</strong>
            <span className="bcr-chip bcr-chip-never">{neverRows.length}</span> = N'a pas connectes
            <span className="bcr-chip bcr-chip-total">{activeRows.length}</span> = Total <strong>{formatMoney(sumTotal(activeRows))}</strong>
            <span className="bcr-scope-note">In {rows.length} Banques</span>
          </div>

          <div className="bcr-grid">
            {columns.length === 0 && <p className="bcr-empty">Aucune banque a afficher.</p>}
            {columns.map((col, ci) => (
              <table key={ci} className="bcr-table">
                <thead>
                  <tr>
                    <th>Banque</th>
                    <th>Dernier mouvement</th>
                    <th>Ventes Total</th>
                  </tr>
                </thead>
                <tbody>
                  {col.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span
                          className={`bcr-numero bcr-numero-${row.status}`}
                          onMouseEnter={(e) => handleEnter(row, e)}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={() => setSelectedBankId(row.id)}
                        >
                          {row.seq}
                        </span>
                      </td>
                      <td className={row.status === 'online' ? 'bcr-time-online' : ''}>{formatTime(row.last_movement)}</td>
                      <td>{formatMoney(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </>
      )}

      {tooltip && (
        <div className="bcr-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="bcr-tooltip-name">{tooltip.row.name}</div>
          <div>Telephone: {tooltip.row.phone || '.'}</div>
          <div>Total des Tickets: {tooltip.row.ticket_count}</div>
          <div>Ventes Total: {formatMoney(tooltip.row.total)}</div>
          <div>Last Sale Temps: {formatTime(tooltip.row.last_movement) || '-'}</div>
        </div>
      )}
    </Layout>
  );
};

export default BanquesConnecteResults;
